<script setup>
/**
 * CrmLoseReasonPopover — popover inline de motivo de perda (T5 / incremento e).
 *
 * Abre ancorado no botão "Marcar Perdido" no rodapé do painel.
 * Confirmar fica disabled até um motivo ser selecionado; quando motivo='outro',
 * também exige texto na textarea antes de liberar.
 * Emite 'confirm' com (motivo, detalhamento|null), ou 'cancel' para fechar.
 *
 * pt-BR hardcoded (rótulos de motivo, hints): INTENCIONAL no v1. CRM é pt-BR
 * locked — sem i18n no v1 (decisão de escopo). Não migrar agora.
 */
import { ref, computed, useId } from 'vue';
import { LOSS_REASONS } from 'dashboard/routes/dashboard/cw_custom/constants/lossReasons.js';

const props = defineProps({
  submitting: { type: Boolean, default: false },
  // FE-H2: erro inline vindo do pai (outcomeError do CrmDealPanel).
  error: { type: String, default: '' },
});

const emit = defineEmits(['confirm', 'cancel']);

// LOSS_REASONS agora vive em constants/lossReasons.js (fonte única, compartilhada
// com o ConfirmLossModal da perda assistida — F5). Slugs/rótulos inalterados.

const selected = ref('');
const outroNote = ref('');

// F-P3 (a11y): id base único por instância para associação explícita label/radio
// (id + for) e para o aria-label da textarea — evita colisão se houver mais de um
// popover montado e remove a dependência do aninhamento implícito do <label>.
const uid = useId();
const reasonId = value => `${uid}-${value}`;

// F-P2: a textarea "Outro" é obrigatória. O botão Confirmar já fica desabilitado,
// mas sem texto visível o usuário não sabe por quê — este hint explica.
const showOutroHint = computed(
  () => selected.value === 'outro' && outroNote.value.trim().length === 0
);

const canConfirm = computed(() => {
  if (!selected.value || props.submitting) return false;
  if (selected.value === 'outro') return outroNote.value.trim().length > 0;
  return true;
});

function confirm() {
  if (!canConfirm.value) return;
  const detalhe = selected.value === 'outro' ? outroNote.value.trim() : null;
  emit('confirm', selected.value, detalhe);
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -- CRM é pt-BR locked (v1) -->
  <!--
    Popover ancorado à direita do botão Perdido, acima do rodapé.
    z-50 garante ficar acima do conteúdo do painel.
  -->
  <div
    class="absolute bottom-full right-0 mb-2 z-50 w-64 bg-n-background border border-n-weak rounded-card shadow-mj-4 flex flex-col"
    role="dialog"
    aria-label="Motivo de perda"
    @click.stop
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-3 pt-3 pb-2">
      <span class="text-mj-13 font-500 text-n-slate-10">
        Por que foi perdido?
      </span>
      <button
        class="size-5 flex items-center justify-center rounded-md text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors duration-mj-toque"
        @click="emit('cancel')"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>

    <!-- Radio list — F-P3: associação explícita label/radio via :id + :for. -->
    <div class="px-3 pb-2 flex flex-col gap-0.5">
      <label
        v-for="reason in LOSS_REASONS"
        :key="reason.value"
        :for="reasonId(reason.value)"
        class="flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer text-mj-13 font-400 text-n-slate-12 transition-colors duration-mj-toque"
        :class="[
          selected === reason.value
            ? 'bg-n-ruby-2 text-n-ruby-11'
            : 'hover:bg-n-alpha-1',
        ]"
      >
        <input
          :id="reasonId(reason.value)"
          v-model="selected"
          type="radio"
          :value="reason.value"
          name="crm-lose-reason"
          class="accent-n-ruby-9 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-7 focus-visible:ring-offset-1 focus-visible:ring-offset-n-background"
        />
        {{ reason.label }}
      </label>

      <!-- Textarea: só visível quando "Outro" selecionado -->
      <textarea
        v-if="selected === 'outro'"
        v-model="outroNote"
        maxlength="400"
        rows="3"
        placeholder="Descreva o motivo…"
        aria-label="Descreva o motivo da perda"
        :aria-invalid="showOutroHint"
        class="mt-1.5 w-full resize-none rounded-lg border border-n-weak bg-n-surface-1 px-2.5 py-2 text-mj-12 font-400 text-n-slate-12 placeholder-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-ruby-7 transition-colors duration-mj-toque"
      />

      <!-- F-P2: hint inline — só quando 'Outro' está selecionado e a nota está vazia. -->
      <p
        v-if="showOutroHint"
        class="mt-1 px-0.5 text-mj-11 font-400 text-n-slate-10"
      >
        Descreva o motivo para confirmar.
      </p>
    </div>

    <!-- Erro inline (FE-H2: prop do pai — sem ref interna morta) -->
    <p v-if="error" class="px-3 pb-1 text-mj-11 text-n-ruby-11">{{ error }}</p>

    <!-- Actions -->
    <div
      class="flex items-center gap-2 px-3 pb-3 pt-1 border-t border-n-weak mt-1"
    >
      <button
        class="flex-1 h-7 text-mj-12 font-500 text-n-slate-11 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors duration-mj-toque"
        @click="emit('cancel')"
      >
        Cancelar
      </button>
      <button
        :disabled="!canConfirm"
        class="flex-1 h-7 text-mj-12 font-500 rounded-lg transition-colors duration-mj-toque"
        :class="[
          canConfirm
            ? 'bg-n-ruby-9 text-white hover:bg-n-ruby-11'
            : 'bg-n-ruby-3 text-n-ruby-8 cursor-not-allowed opacity-60',
        ]"
        @click="confirm"
      >
        {{ props.submitting ? 'Salvando…' : 'Confirmar' }}
      </button>
    </div>
  </div>
</template>
